package fr.frux.feature.treedetail.identity

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.outlined.ArrowForward
import androidx.compose.material.icons.automirrored.outlined.Undo
import androidx.compose.material.icons.outlined.Add
import androidx.compose.material.icons.outlined.Description
import androidx.compose.material.icons.outlined.LocationOn
import androidx.compose.material.icons.outlined.VisibilityOff
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.components.ErrorState
import fr.frux.core.designsystem.components.TreeGlyphDisc
import fr.frux.core.designsystem.error.userMessage
import fr.frux.core.designsystem.icons.FruxSpeciesGlyphs
import fr.frux.feature.treedetail.ImportedTreeSource
import fr.frux.feature.treedetail.resources.Res
import fr.frux.feature.treedetail.resources.treedetail_field_creation
import fr.frux.feature.treedetail.resources.treedetail_field_note
import fr.frux.feature.treedetail.resources.treedetail_field_position
import fr.frux.feature.treedetail.resources.treedetail_field_species
import fr.frux.feature.treedetail.resources.treedetail_history_author_line
import fr.frux.feature.treedetail.resources.treedetail_history_author_unnamed
import fr.frux.feature.treedetail.resources.treedetail_history_changed
import fr.frux.feature.treedetail.resources.treedetail_history_current
import fr.frux.feature.treedetail.resources.treedetail_history_error_title
import fr.frux.feature.treedetail.resources.treedetail_history_import_bordeaux
import fr.frux.feature.treedetail.resources.treedetail_history_import_grenoble
import fr.frux.feature.treedetail.resources.treedetail_history_import_lozere
import fr.frux.feature.treedetail.resources.treedetail_history_import_lyon
import fr.frux.feature.treedetail.resources.treedetail_history_import_metz
import fr.frux.feature.treedetail.resources.treedetail_history_import_montpellier
import fr.frux.feature.treedetail.resources.treedetail_history_import_nantes
import fr.frux.feature.treedetail.resources.treedetail_history_import_osm
import fr.frux.feature.treedetail.resources.treedetail_history_import_paris
import fr.frux.feature.treedetail.resources.treedetail_history_import_plaine_commune
import fr.frux.feature.treedetail.resources.treedetail_history_import_rennes
import fr.frux.feature.treedetail.resources.treedetail_history_import_strasbourg
import fr.frux.feature.treedetail.resources.treedetail_history_import_toulouse
import fr.frux.feature.treedetail.resources.treedetail_history_moved
import fr.frux.feature.treedetail.resources.treedetail_history_moved_back
import fr.frux.feature.treedetail.resources.treedetail_history_no_certainty
import fr.frux.feature.treedetail.resources.treedetail_history_note_added
import fr.frux.feature.treedetail.resources.treedetail_history_note_cleared
import fr.frux.feature.treedetail.resources.treedetail_history_note_edited
import fr.frux.feature.treedetail.resources.treedetail_history_reason
import fr.frux.feature.treedetail.resources.treedetail_history_redacted
import fr.frux.feature.treedetail.resources.treedetail_history_reverted
import fr.frux.feature.treedetail.resources.treedetail_history_subtitle
import fr.frux.feature.treedetail.resources.treedetail_history_title
import fr.frux.feature.treedetail.resources.treedetail_history_truncated
import fr.frux.feature.treedetail.resources.treedetail_identity_footer_moderation
import fr.frux.shared.domain.model.Certainty
import fr.frux.shared.presentation.relativeAgeLabel
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource
import kotlin.time.Clock
import kotlin.time.ExperimentalTime

private val TIMELINE_ICON = 40.dp

/** Feuille « Historique » (canvas `Historique`) : la frise des versions, de la plus récente à la création. */
@Composable
internal fun HistorySheet(
    sheet: IdentitySheet.History,
    onRetry: () -> Unit,
    onClose: () -> Unit,
) {
    IdentitySheetLayout(
        title = stringResource(Res.string.treedetail_history_title),
        subtitle = stringResource(Res.string.treedetail_history_subtitle),
        onClose = onClose,
        spacing = 12,
    ) {
        when (val load = sheet.load) {
            HistoryLoad.Loading -> {
                Box(Modifier.fillMaxWidth().padding(vertical = 32.dp), contentAlignment = Alignment.Center) {
                    CircularProgressIndicator(color = MaterialTheme.colorScheme.primary)
                }
            }

            is HistoryLoad.Failed -> {
                ErrorState(
                    title = stringResource(Res.string.treedetail_history_error_title),
                    message = load.error.userMessage(),
                    onRetry = onRetry,
                )
            }

            is HistoryLoad.Loaded -> {
                Column(modifier = Modifier.padding(top = 4.dp)) {
                    load.rows.forEachIndexed { index, row ->
                        HistoryItem(row, hasNext = index < load.rows.lastIndex || load.truncated)
                    }
                }
                if (load.truncated) {
                    Text(
                        text = stringResource(Res.string.treedetail_history_truncated),
                        style = MaterialTheme.typography.bodyMedium,
                        color = MaterialTheme.colorScheme.onSurfaceVariant,
                        modifier = Modifier.padding(bottom = 16.dp),
                    )
                }
            }
        }
    }
}

@Composable
private fun HistoryItem(
    row: HistoryRow,
    hasNext: Boolean,
) {
    val scheme = MaterialTheme.colorScheme
    Row(modifier = Modifier.height(IntrinsicSize.Min), horizontalArrangement = Arrangement.spacedBy(14.dp)) {
        Column(
            modifier = Modifier.width(TIMELINE_ICON).fillMaxHeight(),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            RowIcon(row)
            if (hasNext) {
                Box(
                    Modifier
                        .padding(top = 4.dp)
                        .width(2.dp)
                        .weight(1f)
                        .background(scheme.outlineVariant),
                )
            }
        }
        Column(
            modifier = Modifier.weight(1f).padding(top = 2.dp, bottom = 20.dp),
            verticalArrangement = Arrangement.spacedBy(6.dp),
        ) {
            RowHeader(row)
            HistoryValue(row)
            Text(
                text = authorLine(row),
                style = MaterialTheme.typography.bodyMedium,
                color = scheme.onSurfaceVariant,
            )
            row.reason?.let { reason ->
                Text(
                    text = stringResource(Res.string.treedetail_history_reason, reason),
                    style = MaterialTheme.typography.bodyMedium,
                    color = scheme.onSurface,
                    modifier =
                        Modifier
                            .background(scheme.surfaceVariant, RoundedCornerShape(12.dp))
                            .padding(horizontal = 12.dp, vertical = 8.dp),
                )
            }
        }
    }
}

@Composable
private fun RowHeader(row: HistoryRow) {
    val scheme = MaterialTheme.colorScheme
    Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
        Text(
            text = fieldLabel(row).uppercase(),
            style = MaterialTheme.typography.labelMedium,
            color = scheme.onSurfaceVariant,
        )
        when {
            row.isRevert -> {
                val label = stringResource(Res.string.treedetail_history_reverted)
                Pill(label, scheme.tertiaryContainer, scheme.onTertiaryContainer)
            }

            row.isCurrent -> {
                val label = stringResource(Res.string.treedetail_history_current)
                Pill(label, scheme.primaryContainer, scheme.onPrimaryContainer)
            }
        }
    }
}

@Composable
private fun Pill(
    text: String,
    background: Color,
    content: Color,
) {
    Text(
        text = text,
        style = MaterialTheme.typography.labelMedium,
        color = content,
        modifier = Modifier.background(background, RoundedCornerShape(50)).padding(horizontal = 10.dp, vertical = 4.dp),
    )
}

@Composable
private fun fieldLabel(row: HistoryRow): String =
    when (row.field) {
        HistoryField.SPECIES -> stringResource(Res.string.treedetail_field_species)
        HistoryField.NOTE -> stringResource(Res.string.treedetail_field_note)
        HistoryField.POSITION -> stringResource(Res.string.treedetail_field_position)
        HistoryField.CREATION -> stringResource(Res.string.treedetail_field_creation)
        HistoryField.OTHER -> (row.change as? HistoryChange.Unknown)?.raw.orEmpty()
    }

/** Disque de la colonne d'icône : espèce, note, position, rétablie, caviardée, création ou inconnue. */
@Composable
private fun RowIcon(row: HistoryRow) {
    val scheme = MaterialTheme.colorScheme
    when {
        row.isRevert -> {
            IconDisc(Icons.AutoMirrored.Outlined.Undo, scheme.tertiaryContainer, scheme.onTertiaryContainer)
        }

        row.change is HistoryChange.Redacted -> {
            IconDisc(Icons.Outlined.VisibilityOff, scheme.surfaceVariant, scheme.onSurfaceVariant)
        }

        row.field == HistoryField.SPECIES -> {
            val name = row.scientificName
            if (name != null) {
                SpeciesDisc(name, null, TIMELINE_ICON)
            } else {
                TreeGlyphDisc(FruxSpeciesGlyphs.Fleur, scheme.secondary, size = TIMELINE_ICON)
            }
        }

        row.field == HistoryField.POSITION -> {
            IconDisc(Icons.Outlined.LocationOn, scheme.surfaceVariant, scheme.primary)
        }

        row.field == HistoryField.CREATION -> {
            IconDisc(Icons.Outlined.Add, scheme.surfaceVariant, scheme.primary)
        }

        else -> {
            IconDisc(Icons.Outlined.Description, scheme.surfaceVariant, scheme.primary)
        }
    }
}

@Composable
private fun IconDisc(
    icon: ImageVector,
    background: Color,
    tint: Color,
) {
    Box(
        modifier = Modifier.size(TIMELINE_ICON).background(background, CircleShape),
        contentAlignment = Alignment.Center,
    ) {
        Icon(imageVector = icon, contentDescription = null, tint = tint, modifier = Modifier.size(20.dp))
    }
}

@Composable
private fun certaintyName(certainty: Certainty?): String =
    certainty?.let { stringResource(it.titleRes()) } ?: stringResource(Res.string.treedetail_history_no_certainty)

@Composable
private fun HistoryValue(row: HistoryRow) {
    val scheme = MaterialTheme.colorScheme
    val bold = SpanStyle(fontWeight = FontWeight.SemiBold, color = scheme.onSurface)
    val muted = SpanStyle(color = scheme.onSurfaceVariant)
    when (val change = row.change) {
        is HistoryChange.SpeciesChanged -> {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                Text(
                    text = change.from,
                    style = MaterialTheme.typography.bodyLarge,
                    color = scheme.onSurfaceVariant,
                    textDecoration = TextDecoration.LineThrough,
                )
                Icon(
                    imageVector = Icons.AutoMirrored.Outlined.ArrowForward,
                    contentDescription = null,
                    modifier = Modifier.size(16.dp),
                    tint = scheme.onSurfaceVariant,
                )
                Text(text = change.to, style = MaterialTheme.typography.bodyLarge, fontWeight = FontWeight.SemiBold)
            }
        }

        is HistoryChange.CertaintyChanged -> {
            val text =
                buildAnnotatedString {
                    withStyle(muted) { append(certaintyName(change.from)) }
                    append(" → ")
                    withStyle(bold) { append(certaintyName(change.to)) }
                }
            Text(text = text, style = MaterialTheme.typography.bodyLarge)
        }

        is HistoryChange.Created -> {
            val certainty = change.certainty?.let { stringResource(it.titleRes()) }
            val text =
                buildAnnotatedString {
                    withStyle(bold) { append(change.speciesName) }
                    if (certainty != null) withStyle(muted) { append(" · $certainty") }
                }
            Text(text = text, style = MaterialTheme.typography.bodyLarge)
        }

        HistoryChange.Redacted -> {
            Text(
                text = stringResource(Res.string.treedetail_history_redacted),
                style = MaterialTheme.typography.bodyLarge,
                color = scheme.onSurfaceVariant,
                fontStyle = FontStyle.Italic,
            )
        }

        is HistoryChange.Moved -> {
            val res =
                if (row.isRevert) {
                    Res.string.treedetail_history_moved_back
                } else {
                    Res.string.treedetail_history_moved
                }
            StrongValue(stringResource(res, change.meters))
        }

        is HistoryChange.NoteChanged -> {
            StrongValue(stringResource(change.movement.label()))
        }

        is HistoryChange.Unknown -> {
            StrongValue(stringResource(Res.string.treedetail_history_changed))
        }
    }
}

@Composable
private fun StrongValue(text: String) {
    Text(
        text = text,
        style = MaterialTheme.typography.bodyLarge,
        fontWeight = FontWeight.SemiBold,
        color = MaterialTheme.colorScheme.onSurface,
    )
}

private fun NoteMovement.label(): StringResource =
    when (this) {
        NoteMovement.ADDED -> Res.string.treedetail_history_note_added
        NoteMovement.EDITED -> Res.string.treedetail_history_note_edited
        NoteMovement.CLEARED -> Res.string.treedetail_history_note_cleared
    }

private fun ImportedTreeSource.historyName(): StringResource =
    when (this) {
        ImportedTreeSource.OSM -> Res.string.treedetail_history_import_osm
        ImportedTreeSource.RENNES -> Res.string.treedetail_history_import_rennes
        ImportedTreeSource.MONTPELLIER -> Res.string.treedetail_history_import_montpellier
        ImportedTreeSource.BORDEAUX -> Res.string.treedetail_history_import_bordeaux
        ImportedTreeSource.LYON -> Res.string.treedetail_history_import_lyon
        ImportedTreeSource.STRASBOURG -> Res.string.treedetail_history_import_strasbourg
        ImportedTreeSource.NANTES -> Res.string.treedetail_history_import_nantes
        ImportedTreeSource.TOULOUSE -> Res.string.treedetail_history_import_toulouse
        ImportedTreeSource.PLAINE_COMMUNE -> Res.string.treedetail_history_import_plaine_commune
        ImportedTreeSource.PARIS -> Res.string.treedetail_history_import_paris
        ImportedTreeSource.GRENOBLE -> Res.string.treedetail_history_import_grenoble
        ImportedTreeSource.METZ -> Res.string.treedetail_history_import_metz
        ImportedTreeSource.LOZERE -> Res.string.treedetail_history_import_lozere
    }

@OptIn(ExperimentalTime::class)
@Composable
private fun authorLine(row: HistoryRow): String {
    val author =
        when (val a = row.author) {
            is HistoryAuthor.Named -> a.name
            HistoryAuthor.Moderation -> stringResource(Res.string.treedetail_identity_footer_moderation)
            is HistoryAuthor.Import -> stringResource(a.source.historyName())
            HistoryAuthor.Unnamed -> stringResource(Res.string.treedetail_history_author_unnamed)
        }
    val age = relativeAgeLabel(row.atMillis, Clock.System.now().toEpochMilliseconds())
    return stringResource(Res.string.treedetail_history_author_line, author, age)
}
